/* eslint-disable @angular-eslint/use-lifecycle-interface */
import { Component, Input, HostListener, OnDestroy } from '@angular/core';
import { ActivatedRoute, NavigationEnd, Router, RouterOutlet } from '@angular/router';
import { CommonModule } from '@angular/common';
import { LoadingIndicatorComponent } from "@shared/components/ui/loadings/loading-indicator/loading-indicator.component";
import { UnloggedHeaderComponent } from "@shared/components/layout/headers/unlogged-header/unlogged-header.component";
import { LoggedHeaderComponent } from "@shared/components/layout/headers/logged-header/logged-header.component";
import { filter, Subject, takeUntil } from 'rxjs';
import { ToastComponent } from "@shared/components/ui/toast/toast.component";
import { ModalComponent } from '@shared/components/ui/modal/modal.component';
import { SocketSwitchService } from '@services/websockets/socketswitch/socketswitch.service';
import { AuthService } from '@services/auth/auth.service';
import { ModalService } from '@core/services/modal/modal.service';
import { LanguageService } from '@services/language/language.service';

@Component({
    selector: 'app-root',
    imports: [CommonModule, RouterOutlet, LoadingIndicatorComponent, UnloggedHeaderComponent, LoggedHeaderComponent, ToastComponent, ModalComponent],
    templateUrl: './app.component.html',
    styleUrl: './app.component.scss'
})
export class AppComponent implements OnDestroy {
  headerType = '';
  listValue = true;
  headerTitle = '';
  disablePadding = false;
  isScrollable = false;
  title = 'ATALAYA';
  subTitle = '';
  autoHideHeader = false;
  fullscreenNoPadding = false;
  private isMouseNearTop = false;
  private readonly destroy$ = new Subject<void>();
  private sessionTranslations: Record<string, string> = {};

  constructor(
    private router: Router,
    private route: ActivatedRoute,
    private socketSwitch: SocketSwitchService,
    private authService: AuthService,
    private modalService: ModalService,
    private langService: LanguageService
  ) { }

  ngOnInit() {
    this.updateRouteData();

    this.langService.getSharedDictionary('session')
      .pipe(takeUntil(this.destroy$))
      .subscribe(data => {
        this.sessionTranslations = data;
      });

    this.router.events.pipe(
      filter(event => event instanceof NavigationEnd)
    ).subscribe(() => {
      this.updateRouteData();
    });

    this.socketSwitch.forceLogout$.pipe(takeUntil(this.destroy$)).subscribe(({ user_id }) => {
      const currentId = Number(this.authService.getUserId());
      if (currentId && currentId === user_id) {
        this.modalService.warning(
          () => this.sessionTranslations['expired_detail'] || 'Your session has expired because your account permissions have been modified.',
          () => this.sessionTranslations['expired_title'] || 'Session Expired',
          { closable: false, backdropClose: false }
        )
          .subscribe(() => this.authService.logout());
      }
    });
  }

  private updateRouteData() {
    let route = this.route.snapshot;
    while (route.firstChild) {
      route = route.firstChild;
    }
    
    this.headerType = route.data['headerType'] ?? '';
    this.listValue = route.data['listValue'];
    this.headerTitle = route.data['headerTitle'] || '';
    this.disablePadding = route.data['disablePadding'];
    this.isScrollable = route.data['scrollable'];
    this.subTitle = route.data['subTitle'] || '';
    this.autoHideHeader = route.data['autoHideHeader'] || false;
    this.fullscreenNoPadding = route.data['fullscreenNoPadding'] || false;
    
    if (!this.autoHideHeader) {
      document.body.classList.remove('auto-hide-header');
      document.body.classList.remove('header-visible');
    }
    if (!this.fullscreenNoPadding) {
      document.body.classList.remove('fullscreen-no-padding');
    }
  }

  ngOnDestroy() {
    this.destroy$.next();
    this.destroy$.complete();
    document.body.classList.remove('auto-hide-header');
    document.body.classList.remove('header-visible');
    document.body.classList.remove('fullscreen-no-padding');
  }

  @HostListener('document:fullscreenchange')
  @HostListener('document:webkitfullscreenchange')
  @HostListener('document:mozfullscreenchange')
  @HostListener('document:MSFullscreenChange')
  onFullscreenChange(): void {
    if (!this.autoHideHeader) return;
    this.updateFullscreenClass();
  }

  @HostListener('document:keydown', ['$event'])
  onKeyDown(event: KeyboardEvent): void {
    if (!this.autoHideHeader) return;
    
    if (event.key === 'F11') {
      event.preventDefault();
      this.togglePageFullscreen();
    }
  }

  @HostListener('document:mousemove', ['$event'])
  onMouseMove(event: MouseEvent): void {
    if (!this.autoHideHeader || !document.body.classList.contains('auto-hide-header')) return;

    if (document.body.classList.contains('no-scroll') || document.body.classList.contains('str-selector-open')) {
      document.body.classList.add('header-visible');
      return;
    }

    const headerTriggerZone = 8;
    
    const header = document.querySelector('app-logged-header, app-unlogged-header');
    const isOverHeader = header ? header.contains(event.target as Node) || 
      (event.clientY <= header.getBoundingClientRect().bottom && document.body.classList.contains('header-visible')) : false;
    
    if (event.clientY <= headerTriggerZone || isOverHeader) {
      document.body.classList.add('header-visible');
      this.isMouseNearTop = true;
    } else {
      setTimeout(() => {
        if (!this.isMouseNearTop && !document.body.classList.contains('no-scroll') && !document.body.classList.contains('str-selector-open')) {
          document.body.classList.remove('header-visible');
        }
      }, 300);
      this.isMouseNearTop = false;
    }
  }

  private togglePageFullscreen(): void {
    if (document.fullscreenElement) {
      document.exitFullscreen();
    } else {
      document.documentElement.requestFullscreen().catch(err => {
        console.error('Error attempting to enable fullscreen:', err);
      });
    }
  }

  private updateFullscreenClass(): void {
    const isFullscreen = !!(document.fullscreenElement || 
      (document as any).webkitFullscreenElement || 
      (document as any).mozFullScreenElement || 
      (document as any).msFullscreenElement);
    
    if (isFullscreen) {
      document.body.classList.add('auto-hide-header');
      if (this.fullscreenNoPadding) {
        document.body.classList.add('fullscreen-no-padding');
      }
    } else {
      document.body.classList.remove('auto-hide-header');
      document.body.classList.remove('header-visible');
      document.body.classList.remove('fullscreen-no-padding');
    }
  }
}
